import { DatePipe } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, NgZone, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ApiLoadErrorComponent } from '../../components/api-load-error/api-load-error.component';
import { InfoTooltipComponent } from '../../components/info-tooltip/info-tooltip.component';
import { AnalyticsApiError, AnalyticsApiService, AnalyticsEventRecord, AnalyticsUserSummary } from '../../services/analytics-api.service';
import { ForegroundRefreshService } from '../../services/foreground-refresh.service';

type AnalyticsDetailTab = 'overview' | 'activity' | 'pages' | 'interactions' | 'conversions' | 'engagement';

@Component({
  selector: 'app-admin-analytics-page',
  standalone: true,
  imports: [DatePipe, FormsModule, ApiLoadErrorComponent, InfoTooltipComponent],
  templateUrl: './admin-analytics-page.component.html',
  styleUrls: ['./admin-analytics-page.component.scss']
})
export class AdminAnalyticsPageComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly ngZone = inject(NgZone);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  isLoading = true;
  isLoadingDetail = false;
  loadError = '';
  detailError = '';
  users: AnalyticsUserSummary[] = [];
  selectedUser: AnalyticsUserSummary | null = null;
  selectedEvents: AnalyticsEventRecord[] = [];
  activeDetailTab: AnalyticsDetailTab = 'overview';
  filters = { search: '', eventType: '', sort: 'most-recent', from: '', to: '' };
  private isBackgroundRefreshInFlight = false;

  readonly eventTypes = [
    { value: '', label: 'All activity' },
    { value: 'page_view', label: 'Page views' },
    { value: 'click', label: 'Clicks' },
    { value: 'focus', label: 'Focus changes' },
    { value: 'scroll', label: 'Scroll checkpoints' },
    { value: 'page_duration', label: 'Page duration' },
    { value: 'form_start', label: 'Form starts' },
    { value: 'form_submit', label: 'Form submits' },
    { value: 'outbound_link', label: 'Outbound links' },
    { value: 'download', label: 'Downloads' },
    { value: 'media_play', label: 'Media starts' },
    { value: 'media_pause', label: 'Media pauses' },
    { value: 'media_complete', label: 'Media completions' },
    { value: 'content_view', label: 'Section views' },
  ];
  readonly detailTabs: Array<{ id: AnalyticsDetailTab; label: string; help: string }> = [
    { id: 'overview', label: 'Overview', help: 'Activity totals and the last observed session state.' },
    { id: 'activity', label: 'Activity', help: 'Chronological event history without typed values or sensitive fields.' },
    { id: 'pages', label: 'Pages', help: 'Page views and dwell events grouped from the selected user history.' },
    { id: 'interactions', label: 'Interactions', help: 'Safe control metadata for clicks, focus changes, and scroll checkpoints.' },
    { id: 'conversions', label: 'Conversions', help: 'Form progress, submissions, outbound links, and downloads without submitted content or destination URLs.' },
    { id: 'engagement', label: 'Engagement', help: 'Section visibility and native audio/video lifecycle events without media URLs.' },
  ];

  constructor(
    private readonly analyticsApi: AnalyticsApiService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly foregroundRefresh: ForegroundRefreshService,
  ) {}

  ngOnInit(): void {
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      const requestedUser = params.get('user');
      if (requestedUser && requestedUser !== this.selectedUser?.publicId) {
        const knownUser = this.users.find((user) => user.publicId === requestedUser);
        if (knownUser) {
          this.openUser(knownUser, false);
        }
      }
    });
    this.loadUsers();
    this.foregroundRefresh.watch(5000).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.loadUsers(true));
  }

  get displayedEvents(): AnalyticsEventRecord[] {
    if (this.activeDetailTab === 'pages') {
      return this.selectedEvents.filter((event) => event.type === 'page_view' || event.type === 'page_duration');
    }
    if (this.activeDetailTab === 'interactions') {
      return this.selectedEvents.filter((event) => ['click', 'focus', 'scroll'].includes(event.type));
    }
    if (this.activeDetailTab === 'conversions') {
      return this.selectedEvents.filter((event) => ['form_start', 'form_submit', 'outbound_link', 'download'].includes(event.type));
    }
    if (this.activeDetailTab === 'engagement') {
      return this.selectedEvents.filter((event) => ['content_view', 'media_play', 'media_pause', 'media_complete'].includes(event.type));
    }
    return this.selectedEvents;
  }

  loadUsers(background = false): void {
    if (background) {
      if (this.isBackgroundRefreshInFlight || this.isLoading || this.isLoadingDetail) return;
      this.isBackgroundRefreshInFlight = true;
    } else {
      this.isLoading = true;
      this.loadError = '';
    }
    this.analyticsApi.getOwnerUsers(this.filters).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (users) => this.runInAngular(() => {
        this.users = users;
        if (!background) this.isLoading = false;
        this.isBackgroundRefreshInFlight = false;
        const requestedUser = this.route.snapshot.queryParamMap.get('user');
        const nextSelected = requestedUser ? users.find((user) => user.publicId === requestedUser) : null;
        if (nextSelected) {
          this.openUser(nextSelected, false);
        } else if (this.selectedUser && !users.some((user) => user.publicId === this.selectedUser?.publicId)) {
          this.clearSelectedUser();
        }
      }),
      error: (error: AnalyticsApiError) => this.runInAngular(() => {
        if (!background) {
          this.loadError = error.message;
          this.isLoading = false;
        }
        this.isBackgroundRefreshInFlight = false;
      })
    });
  }

  openUser(user: AnalyticsUserSummary, syncRoute = true): void {
    if (user.publicId === this.selectedUser?.publicId && this.selectedEvents.length) {
      return;
    }
    this.selectedUser = user;
    this.selectedEvents = [];
    this.detailError = '';
    this.activeDetailTab = 'overview';
    this.isLoadingDetail = true;
    if (syncRoute) {
      void this.router.navigate([], { relativeTo: this.route, queryParams: { user: user.publicId }, queryParamsHandling: 'merge' });
    }
    this.analyticsApi.getOwnerUser(user.publicId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (detail) => this.runInAngular(() => {
        this.selectedUser = detail.summary;
        this.selectedEvents = detail.events;
        this.isLoadingDetail = false;
      }),
      error: (error: AnalyticsApiError) => this.runInAngular(() => {
        this.detailError = error.message;
        this.isLoadingDetail = false;
      })
    });
  }

  setDetailTab(tab: AnalyticsDetailTab): void {
    this.activeDetailTab = tab;
  }

  clearSelectedUser(): void {
    this.selectedUser = null;
    this.selectedEvents = [];
    this.detailError = '';
    void this.router.navigate([], { relativeTo: this.route, queryParams: { user: null }, queryParamsHandling: 'merge' });
  }

  eventLabel(event: AnalyticsEventRecord): string {
    if (event.type === 'page_duration') {
      return `Active for ${this.formatDuration(event.durationMs ?? 0)}`;
    }
    if (event.type === 'scroll') {
      return `Scrolled to ${event.scrollPercent ?? 0}%`;
    }
    if (event.type === 'form_start') { return `Started ${event.elementLabel || 'a form'}`; }
    if (event.type === 'form_submit') { return `Submitted ${event.elementLabel || 'a form'}`; }
    if (event.type === 'outbound_link') { return `Opened external link: ${event.elementLabel || 'link'}`; }
    if (event.type === 'download') { return `Downloaded: ${event.elementLabel || 'file'}`; }
    if (event.type === 'content_view') { return `Viewed section: ${event.elementLabel || 'section'}`; }
    if (event.type === 'media_play') { return `Started media: ${event.elementLabel || 'media'}`; }
    if (event.type === 'media_pause') { return `Paused media: ${event.elementLabel || 'media'}`; }
    if (event.type === 'media_complete') { return `Completed media: ${event.elementLabel || 'media'}`; }
    return event.elementLabel || event.elementKind || event.type.replace('_', ' ');
  }

  formatDuration(value: number): string {
    const seconds = Math.max(0, Math.round(value / 1000));
    return seconds >= 60 ? `${Math.floor(seconds / 60)}m ${seconds % 60}s` : `${seconds}s`;
  }

  private runInAngular(callback: () => void): void {
    this.ngZone.run(() => {
      callback();
      this.changeDetectorRef.detectChanges();
    });
  }
}
